import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const TournamentBreakdown = ({ data }) => {
  const [expandedId, setExpandedId] = useState(null);
  const navigate = useNavigate();

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getStatusColor = (status) => {
    const colors = {
      'upcoming': 'text-warning bg-warning/10',
      'active': 'text-success bg-success/10',
      'completed': 'text-muted-foreground bg-muted/10'
    };
    return colors?.[status] || 'text-muted-foreground bg-muted/10';
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 70) return 'text-success';
    if (accuracy >= 50) return 'text-warning';
    return 'text-error';
  };

  if (!data || data?.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Icon name="Trophy" size={48} className="mx-auto mb-3 opacity-50" />
        <p>No tournament data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data?.map((tournament) => (
        <div
          key={tournament?.id}
          className="border border-border rounded-lg overflow-hidden hover:shadow-elevation-sm transition-smooth"
        >
          {/* Tournament Header */}
          <div
            className="p-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => toggleExpand(tournament?.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-foreground">{tournament?.name}</h3>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(tournament?.status)}`}>
                    {tournament?.status}
                  </span>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-primary/10 text-primary">
                    {tournament?.format}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Accuracy</p>
                    <p className={`font-semibold ${getAccuracyColor(tournament?.accuracy)}`}>
                      {tournament?.accuracy?.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Predictions</p>
                    <p className="font-semibold text-foreground">
                      {tournament?.correctPredictions}/{tournament?.totalPredictions}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Points</p>
                    <p className="font-semibold text-foreground">{tournament?.pointsEarned}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Rank</p>
                    <p className="font-semibold text-foreground">#{tournament?.rank}</p>
                  </div>
                </div>
              </div>
              
              <Icon
                name={expandedId === tournament?.id ? 'ChevronUp' : 'ChevronDown'}
                size={20}
                className="text-muted-foreground ml-4"
              />
            </div>
          </div>

          {/* Expanded Content */}
          {expandedId === tournament?.id && (
            <div className="p-4 border-t border-border bg-card">
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-foreground mb-3">Performance Insights</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-muted/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="Target" size={16} className="text-primary" />
                      <p className="text-xs text-muted-foreground">Success Rate</p>
                    </div>
                    <p className="text-lg font-bold text-foreground">
                      {tournament?.totalPredictions > 0 
                        ? ((tournament?.correctPredictions / tournament?.totalPredictions) * 100)?.toFixed(1)
                        : 0}%
                    </p>
                  </div>
                  
                  <div className="bg-muted/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="Award" size={16} className="text-success" />
                      <p className="text-xs text-muted-foreground">Avg Points/Prediction</p>
                    </div>
                    <p className="text-lg font-bold text-foreground">
                      {tournament?.totalPredictions > 0
                        ? (tournament?.pointsEarned / tournament?.totalPredictions)?.toFixed(1)
                        : 0}
                    </p>
                  </div>
                  
                  <div className="bg-muted/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="TrendingUp" size={16} className="text-warning" />
                      <p className="text-xs text-muted-foreground">Ranking Position</p>
                    </div>
                    <p className="text-lg font-bold text-foreground">#{tournament?.rank}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Eye"
                  onClick={() => navigate(`/tournament-details/${tournament?.id}`)}
                >
                  View Details
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="BarChart3"
                  disabled
                >
                  Match-by-Match Analysis
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default TournamentBreakdown;